import type { InventoryItem } from '@/modules/inventory/api/inventory.api';

export interface ProcedureStep {
  order: number;
  title: string;
  description?: string;
  durationMinutes?: number;
  countsTowardKpi?: boolean;
}

export interface MaterialLine {
  inventoryItemId: string;
  inventoryItem?: {
    id: string;
    itemName: string;
    itemCode: string;
    unit: string;
  };
  quantity: number;
  required: boolean;
}

/** Form row for MaterialLinesEditor (catalog / plan override). */
export interface MaterialLineFormRow {
  key: string;
  inventoryItemId: string;
  item: InventoryItem | null;
  quantity: string;
  required: boolean;
}

export const stepCountsTowardKpi = (step?: Pick<ProcedureStep, 'countsTowardKpi'> | null): boolean =>
  Boolean(step) && step?.countsTowardKpi !== false;

/** Explicit warranty policies. Absence / blank = no warranty. */
export type WarrantyType = 'months' | 'lifetime';

export const WARRANTY_TYPES: WarrantyType[] = ['months', 'lifetime'];

export type WarrantyStatus = 'none' | 'pending' | 'active' | 'expired' | 'lifetime';

/** Legacy `none` and blank → null (no warranty). */
export const resolveWarrantyType = (value?: string | null): WarrantyType | null =>
  value === 'months' || value === 'lifetime' ? value : null;

export const hasWarranty = (value?: string | null): boolean => resolveWarrantyType(value) != null;

/** Catalog list/detail label: "—", "12 tháng", or "Trọn đời". */
export const catalogWarrantyDisplay = (
  type: WarrantyType | null | undefined,
  months: number | undefined,
  labels: { none: string; monthsCount: (count: number) => string; lifetime: string }
): string => {
  const resolved = resolveWarrantyType(type);
  if (resolved === 'lifetime') return labels.lifetime;
  if (resolved === 'months' && months && months >= 1) return labels.monthsCount(months);
  return labels.none;
};

export interface TreatmentCatalogCategoryRef {
  id: string;
  name: string;
  categoryCode: string;
}

export interface TreatmentCatalogUserRef {
  id: string;
  name: string;
}

export interface TreatmentCatalogItem {
  _id: string;
  id?: string;
  name: string;
  code: string;
  category: TreatmentCatalogCategoryRef | string;
  description?: string;
  price: number;
  duration: number;
  uom?: string;
  labOrderEnabled?: boolean;
  warrantyType?: WarrantyType | null;
  warrantyMonths?: number | null;
  defaultLabWorkType?: string | null;
  defaultLabMaterial?: string | null;
  countsTowardKpi?: boolean;
  isActive: boolean;
  notes?: string;
  procedureSteps: ProcedureStep[];
  materialLines?: MaterialLine[];
  createdBy?: TreatmentCatalogUserRef;
  updatedBy?: TreatmentCatalogUserRef;
  createdAt?: string;
  updatedAt?: string;
}

export interface TreatmentCatalogFormData {
  _id?: string;
  name: string;
  category: string;
  description: string;
  price: string;
  duration: string;
  uom: string;
  labOrderEnabled: boolean;
  /** Empty = no warranty. */
  warrantyType: WarrantyType | '';
  warrantyMonths: string;
  defaultLabWorkType: string;
  defaultLabMaterial: string;
  countsTowardKpi: boolean;
  isActive: boolean;
  notes: string;
  procedureSteps: ProcedureStep[];
  materialLines: MaterialLineFormRow[];
}

export interface TreatmentCatalogFormErrors {
  name?: string;
  category?: string;
  price?: string;
  duration?: string;
  uom?: string;
  warrantyMonths?: string;
  procedureSteps?: string;
  materialLines?: string;
}

export interface TreatmentUomOption {
  value: string;
  label: string;
}

export const getUomLabel = (
  value: string | undefined,
  options: TreatmentUomOption[]
): string => {
  if (!value) {
    return '—';
  }

  return options.find((option) => option.value === value)?.label ?? value;
};

/** Map master UOM records into select/label options. */
export const toUomOptions = (
  uoms: Array<{ code: string; name: string }>
): TreatmentUomOption[] => uoms.map((uom) => ({ value: uom.code, label: uom.name }));


export const getCategoryId = (
  category: TreatmentCatalogItem['category'] | undefined
): string => {
  if (!category) return '';
  if (typeof category === 'string') return category;
  return category.id;
};

export const getCategoryName = (
  category: TreatmentCatalogItem['category'] | undefined
): string => {
  if (!category) return '—';
  if (typeof category === 'string') return category;
  return category.name || '—';
};

export function renumberProcedureSteps(steps: ProcedureStep[]): ProcedureStep[] {
  return steps.map((step, index) => ({
    ...step,
    order: index + 1
  }));
}

export function sanitizeProcedureSteps(steps: ProcedureStep[]): ProcedureStep[] {
  return renumberProcedureSteps(
    steps
      .filter((step) => step.title.trim() !== '')
      .map((step) => ({
        ...step,
        countsTowardKpi: step.countsTowardKpi !== false,
      }))
  );
}

export function materialLinesToApiPayload(
  lines: MaterialLineFormRow[]
): Array<{ inventoryItem: string; quantity: number; required: boolean }> {
  return lines
    .filter((line) => line.inventoryItemId && Number(line.quantity) > 0)
    .map((line) => ({
      inventoryItem: line.inventoryItemId,
      quantity: Number(line.quantity),
      required: line.required,
    }));
}

export function materialLinesFromApi(lines?: MaterialLine[] | null): MaterialLineFormRow[] {
  if (!lines?.length) return [];
  return lines.map((line) => ({
    key: `${line.inventoryItemId}-${Math.random()}`,
    inventoryItemId: line.inventoryItemId,
    item: line.inventoryItem
      ? ({
          _id: line.inventoryItem.id,
          itemName: line.inventoryItem.itemName,
          itemCode: line.inventoryItem.itemCode,
          unit: line.inventoryItem.unit,
        } as InventoryItem)
      : null,
    quantity: String(line.quantity),
    required: line.required,
  }));
}
